/**
 * The card's one figure and its line, with the reads the figure needs — and ONLY those.
 *
 * Three reasons take their figure from outside the queue: a lapse counts from the roster's last
 * workout, an invitation names the day it was sent from the roster, and a self-paced plan that
 * closed itself counts its sessions from the execution summary. The roster is the screen's own
 * cached read on every surface that draws a card; the summary is the one the plan block below the
 * heading reads anyway, so the heading costs no request of its own.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { daysSinceLastWorkout, invitationClock, rosterQueryOptions } from '~/entities/client';
import { executionSummaryQueryOptions } from '~/entities/plan-assignment';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';
import { toIsoDate } from '~/shared/lib/calendarDate';

import type { AttentionItem } from '../model/attentionItems';
import { planExecution } from '../model/planExecution';
import { taskContent, type TaskContent, type TaskContentDeps, type TaskHero } from '../model/taskContent';
import { useHomeFormats } from './useHomeFormats';

const ROSTER_REASONS = new Set(['lapsedActivity', 'invitationPending']);

/**
 * @param now the surface's own clock — the same instant its queue sentences were worded against, so
 *   the card's "9 dni" and the Pulpit row's "Bez treningu od 9 dni" cannot disagree.
 */
export function useTaskContent(item: AttentionItem, now: Date): TaskContent {
  const contentLang = useContentLanguage();
  const deps = useContentDeps(now);
  const roster = useQuery({ ...rosterQueryOptions(), enabled: ROSTER_REASONS.has(item.reason) });
  const assignmentId = item.reason === 'planCompleted' ? item.triage.planAssignmentId : null;
  const summary = useQuery(
    executionSummaryQueryOptions(assignmentId ?? 0, contentLang, { enabled: assignmentId !== null }),
  );

  const customerId = item.subjectCustomerId;
  // The one roster entry this card is about, found once; the day count is read off it alone.
  const client =
    customerId === null ? undefined : roster.data?.find((entry) => entry.id === customerId && entry.invitationId == null);
  const invitation =
    item.triage.invitationId === null ? null : invitationClock(roster.data, item.triage.invitationId);

  return taskContent(
    item,
    deps,
    {
      daysSinceLastWorkout:
        customerId === null || client === undefined ? undefined : daysSinceLastWorkout([client], now).get(customerId),
      lastWorkoutDay: client?.lastWorkoutAt?.slice(0, 10) ?? null,
      rosterPending: roster.isPending,
      rosterFailed: roster.isError,
      execution: summary.data === undefined ? null : planExecution(summary.data),
      invitationSentOn: invitation?.sentAt?.slice(0, 10) ?? null,
    },
  );
}

/**
 * The figure the queue's own payload carries, and nothing a read would add — a Talia TAB's figure.
 *
 * The tabs stand over the one open card, so a figure that waited on a read would make every tab of
 * an unselected card a read of its own (the execution summary of a plan nobody opened), and would
 * arrive late in a strip that must not change under the trainer. A kind whose figure needs a read
 * (a lapse's days, a closed plan's sessions) has none on its tab; its card says it.
 */
export function useQueueFigure(item: AttentionItem, now: Date): TaskHero | null {
  return taskContent(item, useContentDeps(now)).hero;
}

function useContentDeps(now: Date): TaskContentDeps {
  const { t, i18n } = useTranslation();
  const formats = useHomeFormats();
  return {
    t: (key, values) => t(key, values),
    todayIso: toIsoDate(now),
    formatWeekday: formats.weekdayDay,
    formatDate: formats.shortDate,
    formatDateTime: formats.dateTime,
    locale: i18n.language,
  };
}
